'use client';

import { useMemo, useRef, useState } from 'react';
import { consumeToolUsage } from '@/lib/browser-tool-usage';

// The standard US 4.0 scale with plus/minus steps. Institutions vary (some cap
// A+ at 4.0, some skip minuses entirely); the page copy says so honestly.
const GRADE_POINTS: Record<string, number> = {
  'A+': 4.0,
  A: 4.0,
  'A-': 3.7,
  'B+': 3.3,
  B: 3.0,
  'B-': 2.7,
  'C+': 2.3,
  C: 2.0,
  'C-': 1.7,
  'D+': 1.3,
  D: 1.0,
  'D-': 0.7,
  F: 0.0,
};

type CourseRow = { id: number; name: string; grade: string; credits: number };

export default function BrowserGpaCalculator() {
  const nextIdRef = useRef(4);
  const usageReported = useRef(false);

  const [rows, setRows] = useState<CourseRow[]>([
    { id: 1, name: '', grade: 'A', credits: 3 },
    { id: 2, name: '', grade: 'B+', credits: 3 },
    { id: 3, name: '', grade: 'A-', credits: 4 },
  ]);

  const result = useMemo(() => {
    const counted = rows.filter((row) => row.credits > 0);
    const totalCredits = counted.reduce((sum, row) => sum + row.credits, 0);
    const points = counted.reduce(
      (sum, row) => sum + (GRADE_POINTS[row.grade] ?? 0) * row.credits,
      0,
    );
    return {
      totalCredits,
      gpa: totalCredits > 0 ? points / totalCredits : 0,
      points,
    };
  }, [rows]);

  function update(id: number, patch: Partial<CourseRow>) {
    setRows((current) => current.map((row) => (row.id === id ? { ...row, ...patch } : row)));
    if (!usageReported.current) {
      usageReported.current = true;
      void consumeToolUsage('gpa-calculator', '').catch(() => undefined);
    }
  }

  return (
    <article className="tool-live-card">
      <div className="tool-live-header">
        <div>
          <h2>Calculate your GPA</h2>
          <p>
            Enter each course with its grade and credit hours; the weighted GPA updates as you type.
            Nothing is stored or sent anywhere.
          </p>
        </div>
        <span className="tool-live-badge">GPA</span>
      </div>

      <div className="tool-live-grid">
        <div className="tool-live-uploader" style={{ gap: '0.5rem' }}>
          {rows.map((row, index) => (
            <div
              key={row.id}
              style={{ alignItems: 'center', display: 'flex', gap: '0.5rem', width: '100%' }}
            >
              <input
                aria-label={`Course ${index + 1} name`}
                onChange={(event) => update(row.id, { name: event.target.value })}
                placeholder={`Course ${index + 1} (optional)`}
                style={{ flex: 2, minWidth: 0 }}
                type="text"
                value={row.name}
              />
              <select
                aria-label={`Course ${index + 1} grade`}
                onChange={(event) => update(row.id, { grade: event.target.value })}
                style={{ flex: 1 }}
                value={row.grade}
              >
                {Object.keys(GRADE_POINTS).map((grade) => (
                  <option key={grade} value={grade}>
                    {grade} ({GRADE_POINTS[grade].toFixed(1)})
                  </option>
                ))}
              </select>
              <input
                aria-label={`Course ${index + 1} credits`}
                max={12}
                min={0}
                onChange={(event) => update(row.id, { credits: Number(event.target.value) })}
                style={{ width: 72 }}
                type="number"
                value={row.credits}
              />
              {rows.length > 1 ? (
                <button
                  aria-label={`Remove course ${index + 1}`}
                  className="button button-ghost"
                  onClick={() => setRows((current) => current.filter((r) => r.id !== row.id))}
                  type="button"
                >
                  ×
                </button>
              ) : null}
            </div>
          ))}

          <button
            className="button button-ghost"
            onClick={() =>
              setRows((current) => [
                ...current,
                { id: nextIdRef.current++, name: '', grade: 'B', credits: 3 },
              ])
            }
            type="button"
          >
            + Add course
          </button>
        </div>

        <aside className="tool-live-panel">
          <div className="tool-live-stats">
            <div>
              <strong style={{ fontSize: '1.8rem' }}>{result.gpa.toFixed(2)}</strong>
              <span>weighted GPA on the 4.0 scale</span>
            </div>
            <div>
              <strong>{result.totalCredits}</strong>
              <span>credit hours across {rows.length} courses</span>
            </div>
            <div>
              <strong>{result.points.toFixed(1)}</strong>
              <span>total grade points</span>
            </div>
          </div>

          <p className="tool-live-panel-note">
            GPA is the credit-weighted average: each grade’s points times its credit hours, summed,
            divided by total credits. This uses the common 4.0 scale with plus and minus steps; your
            institution may cap A+ at 4.0 differently or skip minus grades, so treat the second
            decimal as approximate against an official transcript.
          </p>
        </aside>
      </div>
    </article>
  );
}
